import { readWorkbookImportRegistry, resolveWorksheetOwnedImportId } from './importOwnership'

export type WorkbookExecutionBindingMode = 'none' | 'workbook' | 'import'

export type WorkbookExecutionBinding = {
  mode: WorkbookExecutionBindingMode
  workbookId: string
  backendImportId: string
  attachedImportId: string
  executionScopeId: string
  shouldDefer: boolean
  bindingGeneration: string
}

const normalizeText = (value: any): string => String(value ?? '').trim()

export const isLocalImportId = (importIdRaw: any): boolean => {
  const importId = normalizeText(importIdRaw)
  return importId.startsWith('local-') || importId.startsWith('local_')
}

export const isSyntheticBlankCloudImportId = (candidateRaw: any): boolean => {
  const candidate = normalizeText(candidateRaw).toLowerCase()
  return /^blank(?:[-_]|$)/.test(candidate)
}

export const looksLikeUuidImportSnapshotId = (valueRaw: any): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(normalizeText(valueRaw))

export const resolveExecutionWorkbookId = (workbookIdRaw: any, workbookIrRaw: any): string => {
  const candidates = [
    workbookIdRaw,
    (workbookIrRaw as any)?.meta?.fusegrid_workbook_id,
    (workbookIrRaw as any)?.meta?.cloud_workbook_id,
    (workbookIrRaw as any)?.meta?.workbook_id,
  ]
  for (const candidate of candidates) {
    const text = normalizeText(candidate)
    if (text) return text
  }
  return ''
}

const collectExecutionWorkbookIdCandidates = (workbookIdRaw: any, workbookIrRaw: any): string[] => {
  const out: string[] = []
  const seen = new Set<string>()
  for (const candidate of [
    workbookIdRaw,
    (workbookIrRaw as any)?.meta?.fusegrid_workbook_id,
    (workbookIrRaw as any)?.meta?.cloud_workbook_id,
    (workbookIrRaw as any)?.meta?.workbook_id,
  ]) {
    const text = normalizeText(candidate)
    if (!text || seen.has(text)) continue
    seen.add(text)
    out.push(text)
  }
  return out
}

export const isWorkbookScopedExecutionId = (
  candidateRaw: any,
  workbookIdRaw: any,
  workbookIrRaw: any,
): boolean => {
  const candidate = normalizeText(candidateRaw)
  if (!candidate) return false
  return collectExecutionWorkbookIdCandidates(workbookIdRaw, workbookIrRaw).includes(candidate)
}

export const resolveAttachedExecutionImportId = (workbookIdRaw: any, workbookIrRaw: any): string => {
  const backendImportId = normalizeText((workbookIrRaw as any)?.meta?.backend_import_id)
  if (isWorkbookScopedExecutionId(backendImportId, workbookIdRaw, workbookIrRaw)) return ''
  if (isLocalImportId(backendImportId)) return ''
  if (isSyntheticBlankCloudImportId(backendImportId)) return ''
  return backendImportId
}

export const resolveBackendImportId = (
  workbookIdRaw: any,
  importIdRaw: any,
  workbookIrRaw: any,
): string => {
  const overrideImportId = (typeof globalThis !== 'undefined')
    ? normalizeText((globalThis as any).__FG_BACKEND_IMPORT_ID_OVERRIDE)
    : ''
  if (overrideImportId) return overrideImportId
  const workbookId = resolveExecutionWorkbookId(workbookIdRaw, workbookIrRaw)
  const attachedImportId = resolveAttachedExecutionImportId(workbookIdRaw, workbookIrRaw)
  const importId = normalizeText(importIdRaw)
  if (!importId || isWorkbookScopedExecutionId(importId, workbookIdRaw, workbookIrRaw)) {
    return attachedImportId
  }
  if (workbookId && isSyntheticBlankCloudImportId(importId)) {
    return attachedImportId
  }
  if (isLocalImportId(importId)) {
    return attachedImportId || ''
  }
  return importId
}

export const resolveBackendImportIdForWorksheet = (
  workbookIdRaw: any,
  importIdRaw: any,
  workbookIrRaw: any,
  worksheetSpecRaw: any,
): string => {
  const ownedImportId = resolveWorksheetOwnedImportId(workbookIrRaw, worksheetSpecRaw)
  if (
    ownedImportId &&
    !isWorkbookScopedExecutionId(ownedImportId, workbookIdRaw, workbookIrRaw) &&
    !isLocalImportId(ownedImportId)
  ) {
    return ownedImportId
  }
  return resolveBackendImportId(workbookIdRaw, importIdRaw, workbookIrRaw)
}

export const resolveExecutionScopeId = (
  workbookIdRaw: any,
  importIdRaw: any,
  workbookIrRaw: any,
): string => {
  const workbookId = resolveExecutionWorkbookId(workbookIdRaw, workbookIrRaw)
  return workbookId || normalizeText(importIdRaw) || normalizeText((workbookIrRaw as any)?.meta?.backend_import_id)
}

export const buildBindingGeneration = (
  workbookIdRaw: any,
  workbookIrRaw: any,
  backendImportIdRaw: any,
): string => {
  const workbookId = resolveExecutionWorkbookId(workbookIdRaw, workbookIrRaw)
  const backendImportId = normalizeText(backendImportIdRaw)
  const registry = readWorkbookImportRegistry(workbookIrRaw)
  const entryIds = registry.entries
    .map((entry) => normalizeText(entry.import_id))
    .filter(Boolean)
    .sort()
  return [
    workbookId || 'no-workbook',
    backendImportId || 'workbook-scope',
    normalizeText(registry.active_import_id) || 'no-active',
    entryIds.join('|') || 'no-registry',
  ].join('::')
}

export const resolveWorkbookExecutionBinding = (args: {
  workbookIdRaw: any
  importIdRaw: any
  workbookIrRaw: any
  worksheetSpecRaw?: any
}): WorkbookExecutionBinding => {
  const workbookId = resolveExecutionWorkbookId(args.workbookIdRaw, args.workbookIrRaw)
  const attachedImportId = resolveAttachedExecutionImportId(args.workbookIdRaw, args.workbookIrRaw)
  const backendImportId = args.worksheetSpecRaw
    ? resolveBackendImportIdForWorksheet(args.workbookIdRaw, args.importIdRaw, args.workbookIrRaw, args.worksheetSpecRaw)
    : resolveBackendImportId(args.workbookIdRaw, args.importIdRaw, args.workbookIrRaw)
  const executionScopeId = resolveExecutionScopeId(args.workbookIdRaw, args.importIdRaw, args.workbookIrRaw)
  const mode: WorkbookExecutionBindingMode = backendImportId
    ? 'import'
    : workbookId
      ? 'workbook'
      : 'none'
  return {
    mode,
    workbookId,
    backendImportId,
    attachedImportId,
    executionScopeId,
    shouldDefer: false,
    bindingGeneration: buildBindingGeneration(args.workbookIdRaw, args.workbookIrRaw, backendImportId),
  }
}
